'use client'

import { Text } from '@components/primitives/Text'
import type { TextStyle } from '@components/primitives/Text/fontStyles'
import { classMerge } from '@components/utilities/classMerge'
import type { Color } from '@lib/colors/colors'
import Tippy from '@tippyjs/react'
import type { ReactNode } from 'react'
import type { Placement } from 'tippy.js'
// This is intentionally imported to fix a rare CSS bug with the library
// more info can be found here: https://github.com/with-power/www-withpower-com/pull/10297
import 'tippy.js/dist/tippy.css'

export type TooltipProps = {
  backgroundColor?: Color
  className?: string
  delay?: number | [number, number]
  description?: string | ReactNode
  descriptionClassName?: string
  disabled?: boolean
  duration?: number
  inlineTooltip?: boolean
  interactive?: boolean
  label: string | ReactNode
  labelClassName?: string
  labelStyleName?: TextStyle
  placement?: Placement
  underline?: boolean
}

export const TOOLTIP_BACKGROUND_DEFAULT_COLOR = 'white'

/**
 * @deprecated This component is deprecated, use TooltipWrapper instead
 * This version uses the deprecated Text component, and adds an extra empty span which can break some styles when removed
 * Using TooltipWrapper from here on out will allow us to refactor Tooltip and double back to move TooltipWrapper to index.tsx
 * TODO Refactor: https://linear.app/withpower/issue/POW-14634/refactor-existing-tooltips
 */

export const Tooltip = ({
  backgroundColor = TOOLTIP_BACKGROUND_DEFAULT_COLOR,
  className,
  delay = 0,
  description,
  descriptionClassName,
  disabled = false,
  duration = 0,
  inlineTooltip,
  interactive,
  label,
  labelClassName,
  labelStyleName,
  placement,
  underline = true,
}: TooltipProps) => {
  const bgClass = `bg-${backgroundColor}`
  return (
    <Tippy
      arrow={false}
      className={classMerge('-mb-2 cursor-pointer !bg-transparent', className)}
      content={
        description ? (
          <div
            className={classMerge(
              bgClass,
              `z-tooltip whitespace-normal rounded-lg border border-lightGrey900 p-4 text-left text-neutral800 shadow-lg`,
              descriptionClassName,
            )}
          >
            {description}
          </div>
        ) : undefined
      }
      delay={delay}
      disabled={disabled}
      duration={duration}
      interactive={interactive}
      placement={placement}
    >
      <span>
        <Text
          className={classMerge(
            'cursor-pointer',
            underline ? 'underline' : '',
            inlineTooltip ? 'inline' : '',
            labelClassName,
          )}
          styleName={labelStyleName}
          value={label}
        />
      </span>
    </Tippy>
  )
}
